import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface Product {
  sku: string; asin: string; marketplace: string; title: string;
  localPrice: number; localCurrency: string; priceUsd: number;
  vatPct: number | null; vatAmount: number | null; netRevenue: number;
  cogs: number; referralPct: number | null; referralFee: number | null;
  fbaFee: number | null; totalFees: number | null;
  profit: number | null; margin: number | null; roi: number | null;
  breakEvenUnits: number; recentUnits: number;
}

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;
const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);
const pct = (n: number) => `${n.toFixed(1)}%`;

const MP_FLAGS: Record<string, string> = {
  ATVPDKIKX0DER: '🇺🇸', A2EUQ1WTGCTBG2: '🇨🇦', A1AM78C64UM0Y8: '🇲🇽',
  A1F83G8C2ARO7P: '🇬🇧', A1PA6795UKMFR9: '🇩🇪', A13V1IB3VIYZZH: '🇫🇷',
  APJ6JRA9NG5V4: '🇮🇹', A1RKKUPIHCS9HS: '🇪🇸', A1805IZSGTT6HS: '🇳🇱', A2NODRKZP88ZB9: '🇸🇪',
};
const MP_SHORT: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL', A2NODRKZP88ZB9: 'SE',
};
const CUR_SYM: Record<string, string> = {
  USD: '$', CAD: 'C$', MXN: 'MX$', GBP: '£', EUR: '€', SEK: 'kr',
};
// fmtLocal removed — input field handles local price display

function profitColor(val: number): string {
  if (val > 0) return 'text-green-700';
  if (val < 0) return 'text-red-700';
  return 'text-gray-500';
}

function marginColor(val: number): string {
  if (val >= 30) return 'bg-green-50 text-green-800';
  if (val >= 15) return 'bg-yellow-50 text-yellow-800';
  if (val > 0) return 'bg-orange-50 text-orange-800';
  return 'bg-red-50 text-red-800';
}

export function RoiSimulator() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [selectedMarket, setSelectedMarket] = useState<string>('All');
  const [showLocal, setShowLocal] = useState(false);
  const { displayName, parentNickname } = useNicknames();

  useEffect(() => {
    api.get<{ products: Product[] }>('/sales/roi-simulator')
      .then((r) => setProducts(r.products))
      .finally(() => setLoading(false));
  }, []);

  // Exchange rates for local→USD (approximate from API data)
  const rateMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const p of products) {
      if (p.localPrice > 0 && p.priceUsd > 0) {
        map[p.localCurrency] = p.priceUsd / p.localPrice; // e.g., CAD→USD = 0.72
      }
    }
    return map;
  }, [products]);

  const toUsd = useCallback((localAmount: number, currency: string): number => {
    if (currency === 'USD') return localAmount;
    return localAmount * (rateMap[currency] ?? 1);
  }, [rateMap]);

  // Get the local currency for the filtered market
  const filteredCurrency = useMemo(() => {
    if (selectedMarket === 'All') return null;
    const p = products.find(p => (MP_SHORT[p.marketplace] ?? p.marketplace) === selectedMarket);
    return p?.localCurrency ?? null;
  }, [selectedMarket, products]);

  const fromUsd = useCallback((usdAmount: number): number => {
    if (!filteredCurrency || filteredCurrency === 'USD') return usdAmount;
    const rate = rateMap[filteredCurrency];
    return rate ? usdAmount / rate : usdAmount;
  }, [filteredCurrency, rateMap]);

  const fmtMoney = useCallback((n: number) => {
    if (showLocal && filteredCurrency && filteredCurrency !== 'USD') {
      return `${CUR_SYM[filteredCurrency] ?? filteredCurrency} ${fromUsd(n).toFixed(2)}`;
    }
    return fmtUsd(n);
  }, [showLocal, filteredCurrency, fromUsd]);

  // Recalculate with overridden local price
  const simulate = useCallback((p: Product): Product => {
    const key = `${p.sku}|${p.marketplace}`;
    if (!(key in overrides)) return p;
    const newLocalPrice = overrides[key];
    const newPriceUsd = toUsd(newLocalPrice, p.localCurrency);
    const vatPct = p.vatPct;
    const vatAmount = vatPct ? newPriceUsd - (newPriceUsd / (1 + vatPct / 100)) : 0;
    const netRevenue = newPriceUsd - vatAmount;
    if (p.referralPct == null) {
      return { ...p, localPrice: newLocalPrice, priceUsd: Math.round(newPriceUsd * 100) / 100,
        vatAmount: vatPct ? Math.round(vatAmount * 100) / 100 : null, netRevenue: Math.round(netRevenue * 100) / 100 };
    }
    const referralFee = newPriceUsd * (p.referralPct / 100);
    const totalFees = referralFee + (p.fbaFee ?? 0);
    const profit = netRevenue - p.cogs - totalFees;
    const margin = netRevenue > 0 ? (profit / netRevenue) * 100 : 0;
    const roi = p.cogs > 0 ? (profit / p.cogs) * 100 : 0;
    const breakEvenUnits = profit > 0 ? Math.ceil(p.cogs / profit) : 0;
    return {
      ...p,
      localPrice: newLocalPrice,
      priceUsd: Math.round(newPriceUsd * 100) / 100,
      vatAmount: vatPct ? Math.round(vatAmount * 100) / 100 : null,
      netRevenue: Math.round(netRevenue * 100) / 100,
      referralFee: Math.round(referralFee * 100) / 100,
      totalFees: Math.round(totalFees * 100) / 100,
      profit: Math.round(profit * 100) / 100,
      margin: Math.round(margin * 10) / 10,
      roi: Math.round(roi * 10) / 10,
      breakEvenUnits,
    };
  }, [overrides, toUsd]);

  const handlePriceChange = (sku: string, marketplace: string, value: string) => {
    const key = `${sku}|${marketplace}`;
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setOverrides((prev) => ({ ...prev, [key]: num }));
    }
  };

  const resetPrice = (sku: string, marketplace: string) => {
    const key = `${sku}|${marketplace}`;
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold">ROI Simulator</h1>
        <span className="text-xs text-gray-400">All amounts in {showLocal && filteredCurrency ? filteredCurrency : 'USD'}</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Change the price to simulate projected fees, profit, ROI, and margin. Fee ratios based on last 30 days actuals. Changes are not saved.
      </p>

      {loading && <p className="text-gray-400 text-sm">Loading...</p>}

      {products.length > 0 && (
        <>
        <div className="flex items-center gap-1.5 mb-4">
          {['All', ...Array.from(new Set(products.map(p => MP_SHORT[p.marketplace] ?? p.marketplace))).sort()].map(m => (
            <button key={m} onClick={() => { setSelectedMarket(m); if (m === 'All') setShowLocal(false); }}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${selectedMarket === m ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}>
              {m === 'All' ? 'All' : `${Object.entries(MP_FLAGS).find(([k]) => MP_SHORT[k] === m)?.[1] ?? ''} ${m}`}
            </button>
          ))}
          {selectedMarket !== 'All' && filteredCurrency && filteredCurrency !== 'USD' && (
            <>
              <span className="text-gray-300 mx-1">|</span>
              <button onClick={() => setShowLocal(v => !v)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${showLocal ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}>
                {showLocal ? filteredCurrency : 'USD'}
              </button>
              <span className="text-[10px] text-gray-400">{showLocal ? `Showing ${filteredCurrency}` : 'Showing USD'}</span>
            </>
          )}
        </div>
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[160px]">Product</th>
                <th className="text-center px-2 py-2">Market</th>
                <th className="text-right px-2 py-2">Local Price</th>
                <th className="text-right px-2 py-2">Price ({showLocal && filteredCurrency ? filteredCurrency : 'USD'})</th>
                <th className="text-right px-2 py-2">VAT</th>
                <th className="text-right px-2 py-2">COGS</th>
                <th className="text-right px-2 py-2">Referral %</th>
                <th className="text-right px-2 py-2">Referral Fee</th>
                <th className="text-right px-2 py-2">FBA Fee</th>
                <th className="text-right px-2 py-2">Total Fees</th>
                <th className="text-right px-2 py-2">Profit</th>
                <th className="text-right px-2 py-2">Margin</th>
                <th className="text-right px-2 py-2">ROI</th>
                <th className="text-right px-2 py-2">Break-even</th>
                <th className="text-center px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {products.filter(p => selectedMarket === 'All' || (MP_SHORT[p.marketplace] ?? p.marketplace) === selectedMarket).map((raw) => {
                const p = simulate(raw);
                const key = `${p.sku}|${p.marketplace}`;
                const isOverridden = key in overrides;
                return (
                  <tr key={key} className={`border-b hover:bg-gray-50 ${isOverridden ? 'bg-blue-50/30' : ''}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      {parentNickname(p.sku) && <div className="text-[10px] text-gray-400">{parentNickname(p.sku)}</div>}
                      <div className="font-medium" title={p.sku}>{displayName(p.sku) || p.title?.slice(0, 35) || p.sku}</div>
                    </td>
                    <td className="px-2 py-2 text-center" title={p.marketplace}>
                      <span className="text-sm">{MP_FLAGS[p.marketplace] ?? ''}</span>
                      <span className="text-[10px] text-gray-500 ml-0.5">{MP_SHORT[p.marketplace] ?? p.marketplace.slice(0,2)}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <span className="text-[10px] text-gray-400">{CUR_SYM[raw.localCurrency] ?? '$'}</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`w-20 text-right px-1 py-0.5 border rounded text-xs tabular-nums ${isOverridden ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
                          defaultValue={raw.localPrice}
                          value={isOverridden ? overrides[key] : undefined}
                          onChange={(e) => handlePriceChange(p.sku, p.marketplace, e.target.value)}
                        />
                      </div>
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${isOverridden ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                      {fmtMoney(p.priceUsd)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-gray-400">
                      {p.vatPct != null ? <span title={`${p.vatPct}% VAT`}>{fmtMoney(p.vatAmount!)}</span> : '—'}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(p.cogs)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{p.referralPct != null ? pct(p.referralPct) : '—'}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{p.referralFee != null ? fmtMoney(p.referralFee) : '—'}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{p.fbaFee != null ? fmtMoney(p.fbaFee) : '—'}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium">{p.totalFees != null ? fmtMoney(p.totalFees) : '—'}</td>
                    <td className={`px-2 py-2 text-right tabular-nums font-bold ${p.profit != null ? profitColor(p.profit) : 'text-gray-400'}`}>
                      {p.profit != null ? fmtMoney(p.profit) : '—'}
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${p.margin != null ? marginColor(p.margin) : 'text-gray-400'}`}>
                      {p.margin != null ? pct(p.margin) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{p.roi != null ? pct(p.roi) : '—'}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-gray-500">
                      {p.breakEvenUnits > 0 ? `${fmtNum(p.breakEvenUnits)} units` : '—'}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {isOverridden && (
                        <button onClick={() => resetPrice(p.sku, p.marketplace)}
                          className="text-gray-400 hover:text-gray-600 text-xs">
                          reset
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
